import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, Save, MapPin, Plus, Copy, Trash2, Loader2, Trophy } from "lucide-react";
import exifr from "exifr";
import { compressImage } from "@/lib/imageCompression";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";
import { catchSchema } from "@/lib/validations";
import { format } from "date-fns";
import { FishSpeciesCombobox } from "@/components/catches/FishSpeciesCombobox";

interface CatchEntry {
  id: string;
  fish_species: string;
  date_caught: string;
  weight_lbs: string;
  length_inches: string;
  girth_inches: string;
  general_location: string;
  boat_name: string;
  captain_name: string;
  line_test: string;
  angler_notes: string;
  video_url: string;
  latitude: number | null;
  longitude: number | null;
  trip_id: string | null;
  useTunaCalculation: boolean;
  photoFile: File | null;
  photoPreview: string | null;
}

interface Trip {
  id: string;
  trip_name: string;
  trip_date: string;
  is_public?: boolean;
  user_id?: string;
}

export default function AddCatch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: catchId } = useParams();
  
  // Get tripId from URL query params FIRST
  const queryParams = new URLSearchParams(location.search);
  const tripIdFromUrl = queryParams.get('tripId');
  
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [catchStatus, setCatchStatus] = useState<string>("");
  const [entries, setEntries] = useState<CatchEntry[]>([createEmptyEntry()]);
  const [trips, setTrips] = useState<Trip[]>([]);

  function createEmptyEntry(): CatchEntry {
    return {
      id: Date.now().toString() + Math.random(),
      fish_species: "",
      date_caught: new Date().toISOString().split("T")[0],
      weight_lbs: "",
      length_inches: "",
      girth_inches: "",
      general_location: "",
      boat_name: "",
      captain_name: "",
      line_test: "",
      angler_notes: "",
      video_url: "",
      latitude: null,
      longitude: null,
      trip_id: tripIdFromUrl,
      useTunaCalculation: false,
      photoFile: null,
      photoPreview: null,
    };
  }

  // Fetch user's trips
  useEffect(() => {
    if (user) {
      fetchTrips();
    }
  }, [user]);

  const fetchTrips = async () => {
    try {
      const { data, error } = await supabase
        .from("trips")
        .select("id, trip_name, trip_date")
        .or(`user_id.eq.${user?.id},is_public.eq.true`)
        .order("trip_date", { ascending: false });

      if (error) throw error;
      setTrips((data as any) || []);
    } catch (error) {
      console.error("Error fetching trips:", error);
    }
  };

  // Load catch data if editing
  useEffect(() => {
    if (catchId && user) {
      setIsEditMode(true);
      loadCatchData();
    }
  }, [catchId, user]);

  const loadCatchData = async () => {
    try {
      // Check if user is admin
      const { data: isAdmin } = await supabase.rpc("has_role", {
        _user_id: user?.id,
        _role: "admin",
      });

      // Build query - admins can view any catch, regular users only their own
      let query = supabase
        .from("catches")
        .select("*")
        .eq("id", catchId);

      if (!isAdmin) {
        query = query.eq("angler_id", user?.id);
      }

      const { data, error } = await query.maybeSingle();

      if (error) throw error;
      if (!data) {
        toast.error("Catch not found or you don't have permission to view it");
        navigate("/dashboard");
        return;
      }

      setCatchStatus(data.status);

      setEntries([{
        id: "edit",
        fish_species: data.fish_species || "",
        date_caught: data.date_caught || "",
        weight_lbs: data.weight_lbs?.toString() || "",
        length_inches: data.length_inches?.toString() || "",
        girth_inches: data.girth_inches?.toString() || "",
        general_location: data.general_location || "",
        boat_name: data.boat_name || "",
        captain_name: data.captain_name || "",
        line_test: data.line_test || "",
        angler_notes: data.angler_notes || "",
        video_url: data.video_url || "",
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        trip_id: data.trip_id || null,
        useTunaCalculation: false,
        photoFile: null,
        photoPreview: data.photo_url || null,
      }]);
    } catch (error: any) {
      toast.error("Error loading catch data");
      navigate("/dashboard");
    }
  };

  const addEntry = () => {
    setEntries([...entries, createEmptyEntry()]);
  };

  const duplicateEntry = (id: string) => {
    const entryToDuplicate = entries.find((entry) => entry.id === id);
    if (entryToDuplicate) {
      const duplicated: CatchEntry = {
        ...entryToDuplicate,
        id: Date.now().toString() + Math.random(),
        weight_lbs: "",
        length_inches: "",
        girth_inches: "",
        photoFile: null,
        photoPreview: null,
        angler_notes: "",
      };
      setEntries([...entries, duplicated]);
      toast.success("Catch duplicated - measurements cleared");
    }
  };

  const removeEntry = (id: string) => {
    if (entries.length === 1) {
      toast.error("You must have at least one catch entry");
      return;
    }
    setEntries(entries.filter((entry) => entry.id !== id));
  };

  const updateEntry = (id: string, field: keyof CatchEntry, value: any) => {
    setEntries(
      entries.map((entry) => {
        if (entry.id !== id) return entry;

        const updated = { ...entry, [field]: value };

        // Auto-calculate weight for tuna if checkbox is enabled
        if (
          (field === "girth_inches" || field === "length_inches" || field === "useTunaCalculation") &&
          updated.useTunaCalculation &&
          updated.fish_species.toLowerCase().includes("tuna") &&
          updated.girth_inches &&
          updated.length_inches
        ) {
          const girth = parseFloat(updated.girth_inches);
          const length = parseFloat(updated.length_inches);
          if (!isNaN(girth) && !isNaN(length) && girth > 0 && length > 0) {
            updated.weight_lbs = (((girth * girth) * length) / 800).toFixed(2);
          }
        }

        return updated;
      })
    );
  };

  const reverseGeocode = async (lat: number, lon: number): Promise<string | null> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10`,
        {
          headers: {
            'User-Agent': 'FishingWeightsApp/1.0'
          }
        }
      );
      
      if (!response.ok) return null;
      
      const data = await response.json();
      
      // Prioritize useful location info: city, harbor, bay, ocean area
      const address = data.address || {};
      const locationName = 
        address.harbour || 
        address.bay || 
        address.city || 
        address.town || 
        address.village || 
        address.state || 
        data.display_name?.split(',').slice(0, 2).join(', ');
      
      return locationName || null;
    } catch (error) {
      console.error("Reverse geocoding failed:", error);
      return null;
    }
  };

  const handlePhotoChange = (entryId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Immediately set the photo file and preview - don't wait for EXIF
    const reader = new FileReader();
    reader.onloadend = () => {
      updateEntry(entryId, "photoPreview", reader.result as string);
    };
    reader.onerror = () => {
      toast.error("Failed to read photo file");
    };
    reader.readAsDataURL(file);
    updateEntry(entryId, "photoFile", file);

    // Extract EXIF data in background - don't block photo upload
    exifr.parse(file, {
      gps: true,
      pick: ['DateTimeOriginal', 'CreateDate', 'latitude', 'longitude']
    })
      .then(async (exifData) => {
        console.log("EXIF data extracted:", exifData);
        
        if (!exifData) {
          console.log("No EXIF data in photo");
          toast.info("No GPS data found in photo", {
            description: "You can manually enter the location"
          });
          return;
        }

        // Extract GPS coordinates
        if (exifData.latitude && exifData.longitude) {
          console.log("GPS coordinates found:", exifData.latitude, exifData.longitude);
          updateEntry(entryId, "latitude", exifData.latitude);
          updateEntry(entryId, "longitude", exifData.longitude);
          
          // Reverse geocode to get location name
          try {
            const locationName = await reverseGeocode(exifData.latitude, exifData.longitude);
            
            if (locationName) {
              console.log("Location name found:", locationName);
              updateEntry(entryId, "general_location", locationName);
              toast.success(`Location Found: ${locationName}`, {
                description: "GPS coordinates extracted from photo"
              });
            } else {
              console.log("Reverse geocoding returned no location name");
              toast.success("GPS coordinates extracted from photo");
            }
          } catch (error) {
            console.error("Reverse geocoding failed:", error);
            toast.warning("GPS found but couldn't determine location name", {
              description: "You can manually enter the location"
            });
          }
        } else {
          console.log("Photo has EXIF but no GPS coordinates");
          toast.info("No GPS data found in photo", {
            description: "You can manually enter the location"
          });
        }

        // Extract date
        const photoDate = exifData.DateTimeOriginal || exifData.CreateDate;
        if (photoDate && !isEditMode) {
          try {
            const dateString = new Date(photoDate).toISOString().split("T")[0];
            updateEntry(entryId, "date_caught", dateString);
            toast.success("Date extracted from photo!");
          } catch (error) {
            console.error("Failed to parse photo date:", error);
          }
        }
      })
      .catch((error) => {
        console.error("EXIF parsing failed:", error);
        toast.info("Couldn't read photo metadata", {
          description: "You can manually enter the location and date"
        });
      });
  };

  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate all entries
      for (const entry of entries) {
        const validationData = {
          fish_species: entry.fish_species,
          date_caught: entry.date_caught,
          weight_lbs: entry.weight_lbs,
          length_inches: entry.length_inches || undefined,
          girth_inches: entry.girth_inches || undefined,
          general_location: entry.general_location,
          boat_name: entry.boat_name || undefined,
          captain_name: entry.captain_name || undefined,
          line_test: entry.line_test || undefined,
          angler_notes: entry.angler_notes || undefined,
        };

        const result = catchSchema.safeParse(validationData);
        if (!result.success) {
          const firstError = result.error.errors[0];
          toast.error(`Validation error: ${firstError.message}`, {
            description: `Issue with ${firstError.path.join('.')}`,
          });
          setLoading(false);
          return;
        }
      }

      if (isEditMode) {
        // Edit mode: single entry
        const entry = entries[0];
        let photoUrl = entry.photoPreview;

        if (entry.photoFile && user) {
          const compressedFile = await compressImage(entry.photoFile);
          const fileExt = compressedFile.name.split(".").pop();
          const fileName = `${user.id}/${Date.now()}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from("catch-photos")
            .upload(fileName, compressedFile);

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from("catch-photos")
            .getPublicUrl(fileName);

          photoUrl = publicUrl;
        }

        const catchData = catchStatus === 'verified' 
          ? {
              photo_url: photoUrl,
              angler_notes: entry.angler_notes || null,
              video_url: entry.video_url || null,
            }
          : {
              photo_url: photoUrl,
              fish_species: entry.fish_species,
              date_caught: entry.date_caught,
              weight_lbs: parseFloat(entry.weight_lbs),
              length_inches: entry.length_inches ? parseFloat(entry.length_inches) : null,
              girth_inches: entry.girth_inches ? parseFloat(entry.girth_inches) : null,
              general_location: entry.general_location,
              boat_name: entry.boat_name || null,
              captain_name: entry.captain_name || null,
              line_test: entry.line_test || null,
              angler_notes: entry.angler_notes || null,
              video_url: entry.video_url || null,
              latitude: entry.latitude,
              longitude: entry.longitude,
              trip_id: entry.trip_id,
            };

        const { error } = await supabase
          .from("catches")
          .update(catchData)
          .eq("id", catchId)
          .eq("angler_id", user?.id);

        if (error) throw error;
        
        // Show success animation
        setShowSuccess(true);
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        toast.success("Catch updated successfully!");
      } else {
        // Create mode: multiple entries
        const catchesWithPhotos = await Promise.all(
          entries.map(async (entry) => {
            let photo_url = null;

          if (entry.photoFile) {
            const compressedFile = await compressImage(entry.photoFile);
            const fileExt = compressedFile.name.split(".").pop();
            const fileName = `${user?.id}/${Date.now()}_${Math.random()}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
              .from("catch-photos")
              .upload(fileName, compressedFile);

              if (uploadError) throw uploadError;

              const { data: urlData } = supabase.storage
                .from("catch-photos")
                .getPublicUrl(fileName);

              photo_url = urlData.publicUrl;
            }

            return {
              angler_id: user?.id,
              fish_species: entry.fish_species,
              date_caught: entry.date_caught,
              weight_lbs: parseFloat(entry.weight_lbs),
              length_inches: entry.length_inches ? parseFloat(entry.length_inches) : null,
              girth_inches: entry.girth_inches ? parseFloat(entry.girth_inches) : null,
              general_location: entry.general_location,
              boat_name: entry.boat_name || null,
              captain_name: entry.captain_name || null,
              line_test: entry.line_test || null,
              angler_notes: entry.angler_notes || null,
              video_url: entry.video_url || null,
              latitude: entry.latitude,
              longitude: entry.longitude,
              trip_id: entry.trip_id,
              photo_url,
            };
          })
        );

        const { error } = await supabase.from("catches").insert(catchesWithPhotos);

        if (error) throw error;
        
        // Track analytics event
        trackEvent({ 
          eventName: AnalyticsEvents.CATCH_SUBMITTED, 
          properties: { count: entries.length } 
        });
        
        // Show success animation
        setShowSuccess(true);
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        toast.success(`${entries.length} catch${entries.length > 1 ? "es" : ""} recorded successfully!`);
      }

      navigate("/dashboard");
    } catch (error: any) {
      const errorMessage = error.message || `Failed to ${isEditMode ? 'update' : 'record'} catch`;
      toast.error(errorMessage, {
        description: "Please check your connection and try again.",
        action: {
          label: "Retry",
          onClick: () => handleSubmit(new Event('submit') as any)
        }
      });
      console.error("Catch submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const isVerified = catchStatus === 'verified';
  const isTuna = (species: string) => species.toLowerCase().includes("tuna");

  return (
    <div className="min-h-screen bg-background">
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-card p-8 rounded-lg shadow-2xl animate-scale-in text-center space-y-4">
            <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto animate-scale-in">
              <Trophy className="h-10 w-10 text-success-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-gradient">Success!</h2>
            <p className="text-muted-foreground">
              Your catch has been {isEditMode ? 'updated' : 'recorded'}!
            </p>
          </div>
        </div>
      )}
      <Header />
      
      <main className="container py-8 max-w-6xl">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-3xl text-gradient">
                  {isEditMode ? "Edit Catch" : "Record Catches"}
                </CardTitle>
                {isVerified && (
                  <p className="text-sm text-muted-foreground mt-2">
                    This catch is verified. You can only edit the photo and notes.
                  </p>
                )}
              </div>
              {!isEditMode && (
                <Button
                  type="button"
                  onClick={addEntry}
                  variant="outline"
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Another Catch
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">{entries.map((entry, index) => (
                <div
                  key={entry.id}
                  className="p-6 border-2 border-border rounded-lg space-y-6 relative"
                >
                  {!isEditMode && entries.length > 1 && (
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Catch #{index + 1}</h3>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => duplicateEntry(entry.id)}
                          className="gap-2"
                        >
                          <Copy className="h-4 w-4" />
                          Duplicate
                        </Button>
                        {entries.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeEntry(entry.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <Label htmlFor={`photo-${entry.id}`}>Catch Photo</Label>
                    <div className="mt-2">
                      {entry.photoPreview ? (
                        <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
                          <img src={entry.photoPreview} alt="Preview" className="w-full h-full object-cover" />
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="absolute bottom-2 right-2"
                            onClick={() => {
                              updateEntry(entry.id, "photoFile", null);
                              updateEntry(entry.id, "photoPreview", null);
                            }}
                          >
                            Change Photo
                          </Button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center aspect-video rounded-lg border-2 border-dashed border-border bg-muted/50 cursor-pointer hover:bg-muted transition-colors">
                          <Camera className="h-12 w-12 text-muted-foreground mb-2" />
                          <span className="text-sm text-muted-foreground">Click to upload photo</span>
                          <input
                            id={`photo-${entry.id}`}
                            type="file"
                            accept="image/*"
                            onChange={(e) => handlePhotoChange(entry.id, e)}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {!isVerified && (
                    <>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor={`species-${entry.id}`}>Fish Species *</Label>
                          <FishSpeciesCombobox
                            id={`species-${entry.id}`}
                            value={entry.fish_species}
                            onValueChange={(value) => updateEntry(entry.id, "fish_species", value)}
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`date-${entry.id}`}>Date Caught *</Label>
                          <Input
                            id={`date-${entry.id}`}
                            type="date"
                            value={entry.date_caught}
                            onChange={(e) => updateEntry(entry.id, "date_caught", e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`trip-${entry.id}`}>Fishing Trip (Optional)</Label>
                        <Select
                          value={entry.trip_id || "none"}
                          onValueChange={(value) => updateEntry(entry.id, "trip_id", value === "none" ? null : value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a trip" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No trip selected</SelectItem>
                            {trips.map((trip) => (
                              <SelectItem key={trip.id} value={trip.id}>
                                {trip.trip_name} - {format(new Date(trip.trip_date), "PP")}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {isTuna(entry.fish_species) && (
                        <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
                          <Checkbox 
                            id={`tuna-calc-${entry.id}`}
                            checked={entry.useTunaCalculation}
                            onCheckedChange={(checked) => updateEntry(entry.id, "useTunaCalculation", checked as boolean)}
                          />
                          <Label htmlFor={`tuna-calc-${entry.id}`} className="cursor-pointer font-normal">
                            Tuna Calculation: Calculate weight using ((Girth × Girth) × Length) / 800
                          </Label>
                        </div>
                      )}

                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <Label htmlFor={`weight-${entry.id}`}>Weight (lbs) *</Label>
                          <Input
                            id={`weight-${entry.id}`}
                            type="number"
                            step="0.01"
                            min="0.01"
                            max="10000"
                            value={entry.weight_lbs}
                            onChange={(e) => updateEntry(entry.id, "weight_lbs", e.target.value)}
                            required
                            placeholder="0.00"
                            readOnly={entry.useTunaCalculation && isTuna(entry.fish_species)}
                            className={entry.useTunaCalculation && isTuna(entry.fish_species) ? "bg-muted" : ""}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`length-${entry.id}`}>
                            Length (inches) {entry.useTunaCalculation && isTuna(entry.fish_species) && "*"}
                          </Label>
                          <Input
                            id={`length-${entry.id}`}
                            type="number"
                            step="0.1"
                            min="0"
                            max="500"
                            value={entry.length_inches}
                            onChange={(e) => updateEntry(entry.id, "length_inches", e.target.value)}
                            placeholder="0.0"
                            required={entry.useTunaCalculation && isTuna(entry.fish_species)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`girth-${entry.id}`}>
                            Girth (inches) {entry.useTunaCalculation && isTuna(entry.fish_species) && "*"}
                          </Label>
                          <Input
                            id={`girth-${entry.id}`}
                            type="number"
                            step="0.1"
                            min="0"
                            max="500"
                            value={entry.girth_inches}
                            onChange={(e) => updateEntry(entry.id, "girth_inches", e.target.value)}
                            placeholder="0.0"
                            required={entry.useTunaCalculation && isTuna(entry.fish_species)}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`location-${entry.id}`}>Location *</Label>
                        <Input
                          id={`location-${entry.id}`}
                          value={entry.general_location}
                          onChange={(e) => updateEntry(entry.id, "general_location", e.target.value)}
                          required
                          maxLength={200}
                          placeholder="e.g., Gulf of Mexico, Pacific Coast"
                        />
                        {entry.latitude && entry.longitude && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            GPS: {entry.latitude.toFixed(6)}, {entry.longitude.toFixed(6)}
                          </p>
                        )}
                      </div>

                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <Label htmlFor={`boat-${entry.id}`}>Boat Name</Label>
                          <Input
                            id={`boat-${entry.id}`}
                            value={entry.boat_name}
                            onChange={(e) => updateEntry(entry.id, "boat_name", e.target.value)}
                            maxLength={100}
                            placeholder="Optional"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`captain-${entry.id}`}>Captain</Label>
                          <Input
                            id={`captain-${entry.id}`}
                            value={entry.captain_name}
                            onChange={(e) => updateEntry(entry.id, "captain_name", e.target.value)}
                            maxLength={100}
                            placeholder="Optional"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`line-${entry.id}`}>Line Test</Label>
                          <Input
                            id={`line-${entry.id}`}
                            value={entry.line_test}
                            onChange={(e) => updateEntry(entry.id, "line_test", e.target.value)}
                            maxLength={50}
                            placeholder="e.g., 30 lb test"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor={`video-${entry.id}`}>Video URL</Label>
                    <Input
                      id={`video-${entry.id}`}
                      type="url"
                      value={entry.video_url}
                      onChange={(e) => updateEntry(entry.id, "video_url", e.target.value)}
                      placeholder="https://youtube.com/... or https://vimeo.com/..."
                    />
                    <p className="text-xs text-muted-foreground">
                      Add a link to a video of this catch (opens in new tab)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`notes-${entry.id}`}>Notes</Label>
                    <Textarea
                      id={`notes-${entry.id}`}
                      value={entry.angler_notes}
                      onChange={(e) => updateEntry(entry.id, "angler_notes", e.target.value)}
                      maxLength={5000}
                      placeholder="Add any additional notes about this catch"
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      {entry.angler_notes.length} / 5000 characters
                    </p>
                  </div>
                </div>
              ))}

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 ocean-gradient gap-2">
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {isEditMode ? "Updating..." : "Saving..."}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {isEditMode ? "Update Catch" : `Save ${entries.length} Catch${entries.length > 1 ? "es" : ""}`}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}